/**
 * frontend/src/pages/valuation/_CompanyProfileSection.tsx
 *
 * VAL-PATH-COMPANY (P3) — the company-profile fields of step 1, split out of
 * ValuationCompany.tsx (docs/FILE_SIZE_POLICY.md) before the particulars
 * landed there. Moved as-is: same fields, labels, order and ids. State and
 * the save stay in ValuationCompany.tsx.
 */
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { CompanyRows } from './_CompanyRows'
import { HelpDot } from '@/components/ui/help-dot'
import { HELP_TEXTS, type HelpKey } from './_helpTexts'
import { BLANK_PERSON, joinName, pctTotal, type CompanyState } from './_companyProfile'
import type { incompletePersonMessages, missingPctMessages } from './_companyProfile'
import type { AgreementOffer } from './_operatingAgreement'

const TEXT_AREAS: Array<{ key: keyof CompanyState & string; label: string }> = [
  { key: 'business_description', label: 'Business description' },
  { key: 'history', label: 'History' },
  { key: 'restrictions_agreements', label: 'Restrictions and agreements' },
  { key: 'facilities', label: 'Facilities' },
  { key: 'prior_sales', label: 'Prior sales of ownership interests' },
  { key: 'geography', label: 'Geography' },
  { key: 'assets_liabilities_included', label: 'Assets and liabilities included' },
]

function Field({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <label htmlFor={id} className="text-sm font-medium text-gray-900">{label}</label>
        <HelpDot label={label} paragraphs={HELP_TEXTS[id as HelpKey]} testId={`info-${id}`} />
      </div>
      {children}
    </div>
  )
}

type RowErrors = ReturnType<typeof missingPctMessages>
type PersonErrors = ReturnType<typeof incompletePersonMessages>

export function CompanyProfileSection({
  form,
  set,
  tried,
  mixErrors,
  ownerErrors,
  personErrors,
  offer,
  onUseAgreement,
  ownerNote,
  personNote,
}: {
  form: CompanyState
  set: <K extends keyof CompanyState>(key: K, value: CompanyState[K]) => void
  tried: boolean
  mixErrors: RowErrors
  ownerErrors: RowErrors
  personErrors: PersonErrors
  offer: AgreementOffer
  onUseAgreement: () => void
  ownerNote: (row: { name: string }) => string[]
  personNote: (row: { name: string }) => string[]
}) {
  const textAreas = (from: number, to?: number) => TEXT_AREAS.slice(from, to).map(f => (
    <Field key={f.key} id={`co-${f.key}`} label={f.label}>
      <Textarea id={`co-${f.key}`} value={form[f.key] as string} onChange={e => set(f.key, e.target.value as never)} />
    </Field>
  ))

  return (
    <>
      {textAreas(0, 2)}
      <CompanyRows
        title="Revenue mix"
        help="co-revenue_mix"
        singular="Revenue"
        rows={form.revenue_mix}
        columns={[
          { key: 'line', label: 'line', placeholder: 'Product or service line' },
          { key: 'pct', label: '%', placeholder: '%', numeric: true },
        ]}
        blank={{ line: '', pct: '' }}
        total={pctTotal(form.revenue_mix)}
        rowErrors={tried ? mixErrors : undefined}
        onChange={rows => set('revenue_mix', rows)}
      />
      {offer.readFiles.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
          <span>{`Read from your operating agreement (${offer.readFiles.join(', ')}). Check the names and roles, then save.`}</span>
          <Button type="button" variant="outline" size="sm" onClick={onUseAgreement}>Use these</Button>
        </div>
      )}
      {offer.notRead && (
        <p className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
          We found your operating agreement but could not read it. Enter owners and key people below.
        </p>
      )}
      <CompanyRows
        title="Owners"
        help="co-owners"
        singular="Owner"
        rows={form.owners}
        columns={[
          { key: 'name', label: 'name', placeholder: 'Owner name' },
          { key: 'pct', label: '%', placeholder: '%', numeric: true },
        ]}
        blank={{ name: '', pct: '' }}
        total={pctTotal(form.owners)}
        rowErrors={tried ? ownerErrors : undefined}
        note={ownerNote}
        onChange={rows => set('owners', rows)}
      />
      <Field id="co-legal_entity_type" label="Legal entity type">
        <Input id="co-legal_entity_type" value={form.legal_entity_type} onChange={e => set('legal_entity_type', e.target.value)} />
      </Field>
      {textAreas(2, 4)}
      <CompanyRows
        title="Key people"
        help="co-key_people"
        singular="Person"
        rows={form.key_people}
        columns={[
          { key: 'first', label: 'first name', placeholder: 'First name' },
          { key: 'last', label: 'last name', placeholder: 'Last name' },
          { key: 'role', label: 'role', placeholder: 'Role' },
          { key: 'contact', label: 'phone or email', placeholder: 'Phone or email' },
        ]}
        blank={BLANK_PERSON}
        addLabel="Add row"
        rowErrors={tried ? personErrors : undefined}
        note={row => personNote({ name: joinName(row.first, row.last) })}
        onChange={rows => set('key_people', rows)}
      />
      {textAreas(4)}
      <Field id="co-transaction_structure" label="Transaction structure">
        <select
          id="co-transaction_structure"
          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          value={form.transaction_structure}
          onChange={e => set('transaction_structure', e.target.value as CompanyState['transaction_structure'])}
        >
          <option value="">Not stated</option>
          <option value="asset">Asset</option>
          <option value="stock">Stock</option>
          <option value="n/a">Not applicable</option>
        </select>
      </Field>
    </>
  )
}
